// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
// @todo: Migrate to 'TextField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useEffect} from 'react';

import {useViewer} from '../../../../state/viewer/hooks';
import {BoldLabel, ErrorText, SmallText} from './shared';

export type RunFilter = {
  isRunNameFilterEnabled: boolean;
  runName?: string;
  isUsernameFilterEnabled: boolean;
  username: string | undefined;
};

export type RunFilterConfig = Partial<RunFilter> &
  Pick<RunFilter, 'isRunNameFilterEnabled' | 'isUsernameFilterEnabled'>;

export type RunFilterActions = {
  setRunName: (runName: string) => void;
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => void;
  setUsername: (username: string | undefined) => void;
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => void;
};

// Note that for right now, RunFilterChunk does not have its own zustand store,
// but if the logic for the run filter becomes more complex, it may be a good idea to add one.

export const RunFilterChunk = ({
  runFilter,
  actions,
}: {
  runFilter: RunFilterConfig;
  actions: RunFilterActions;
}) => {
  const viewer = useViewer();
  const viewerUsername = viewer?.username;
  useEffect(() => {
    if (viewerUsername && runFilter.username == null) {
      actions.setUsername(viewerUsername);
    }
    // we only want to run this effect once when the component mounts,
    // but need to make sure viewerUsername is valid when doing so
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerUsername]);
  const showRunNameError =
    runFilter.isRunNameFilterEnabled &&
    runFilter.runName !== undefined &&
    !isValidRunNameFilter(runFilter.isRunNameFilterEnabled, runFilter.runName);
  const showUsernameError =
    runFilter.isUsernameFilterEnabled &&
    runFilter.username !== undefined &&
    !isValidUsernameFilter(runFilter);
  return (
    <>
      <div className="flex flex-row">
        <div className="flex-1">
          <BoldLabel>Filter to one user's runs</BoldLabel>
          <SmallText>
            Trigger this event only on runs created by a given user.
          </SmallText>
        </div>
        <div className="flex-0">
          <Switch.Root
            size="medium"
            checked={runFilter.isUsernameFilterEnabled}
            onCheckedChange={isChecked =>
              actions.setIsUsernameFilterEnabled(isChecked)
            }>
            <Switch.Thumb
              size="medium"
              checked={runFilter.isUsernameFilterEnabled}
            />
          </Switch.Root>
        </div>
      </div>
      <div className="mt-8">
        <TextField
          value={runFilter.username || ''}
          onChange={value => actions.setUsername(value)}
          placeholder="Ex. jane.doe"
          disabled={!runFilter.isUsernameFilterEnabled}
        />
        {showUsernameError && <ErrorText>Username cannot be empty</ErrorText>}
      </div>

      <div className="mt-16 flex flex-row">
        <div className="flex-1">
          <BoldLabel>Filter on run name</BoldLabel>
          <SmallText>
            {/* yes, requested ux text is to use the word "trigger" here */}
            Trigger this event only on runs whose name contains the following
            regex string.
          </SmallText>
        </div>
        <div className="flex-0">
          <Switch.Root
            size="medium"
            checked={runFilter.isRunNameFilterEnabled}
            onCheckedChange={isChecked =>
              actions.setIsRunNameFilterEnabled(isChecked)
            }>
            <Switch.Thumb
              size="medium"
              checked={runFilter.isRunNameFilterEnabled}
            />
          </Switch.Root>
        </div>
      </div>
      <div className="mt-8">
        <TextField
          value={
            runFilter.isRunNameFilterEnabled ? runFilter.runName || '' : ''
          }
          onChange={value => actions.setRunName(value)}
          placeholder="Ex. run-name-.*"
          disabled={!runFilter.isRunNameFilterEnabled}
        />
        {showRunNameError && <ErrorText>Run name cannot be empty</ErrorText>}
      </div>
    </>
  );
};

export const isValidRunFilterConfig = (runFilter: RunFilterConfig) => {
  return (
    isValidUsernameFilter(runFilter) &&
    isValidRunNameFilter(runFilter.isRunNameFilterEnabled, runFilter.runName)
  );
};

const isValidRunNameFilter = (
  isRunNameFilterEnabled: boolean,
  runName: string | undefined
) => {
  return !isRunNameFilterEnabled || (runName != null && runName !== '');
};

const isValidUsernameFilter = (runFilter: RunFilterConfig) => {
  return (
    !runFilter.isUsernameFilterEnabled ||
    (runFilter.username != null && runFilter.username !== '')
  );
};

export const getRunMetricRunFilterString = (runFilter: Partial<RunFilter>) => {
  const rawStr = getRunFilterString(runFilter);
  if (rawStr.length > 0) {
    return `runs ${rawStr} have an `;
  }
  return '';
};

export const getRunFilterString = (runFilter: Partial<RunFilter>) => {
  const runNameSnippet = runFilter.isRunNameFilterEnabled
    ? `matching "${runFilter.runName}"`
    : '';
  const usernameSnippet = runFilter.isUsernameFilterEnabled
    ? `by ${runFilter.username}`
    : '';
  if (runNameSnippet.length > 0 || usernameSnippet.length > 0) {
    return [runNameSnippet, usernameSnippet]
      .filter(s => s.length > 0)
      .join(' ');
  }
  return '';
};

export const getRunFilterConfigFromTriggerFilter = (
  parsedFilter: any
): RunFilterConfig | undefined => {
  const runFilterRaw = JSON.parse(parsedFilter.run_filter);

  if (!runFilterRaw) {
    return undefined;
  }

  // Convert the mongo filter back to our RunFilter type
  const runNameFilter = runFilterRaw.$and?.find(
    (f: any) => f?.displayName?.$regex != null
  );
  const usernameFilter = runFilterRaw.$and?.find(
    (f: any) => f?.username != null
  );

  const runFilter: RunFilterConfig = {
    isRunNameFilterEnabled: runNameFilter != null,
    runName: runNameFilter?.displayName?.$regex,
    isUsernameFilterEnabled: usernameFilter != null,
    username: usernameFilter?.username,
  };

  return runFilter;
};
